import { Injectable, inject } from '@angular/core';
import { FetchPolicy } from '@apollo/client/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { GraphqlHostService } from '../host/graphql-host.service';
import { AuthenticationService } from '@auth/services/authentication.service';
import { environment } from '../../../../environments/environment';
import {
  ParsedAccountSelectorOptions,
  ProfileData,
  RoleSelectorRetrievedData,
  UserById,
  UsersInOrgEntity,
} from '@shared/types/auth/user-organization-role.type';
import {
  OrganizationRole,
  UserOrganization,
} from '@shared/types/auth/mapped-user-organization-role.type';
import { OrganizationData } from '@shared/types/auth/authorized-organization.type';
import { userOrganization } from '@shared/graphql/gql-user-organization';
import { SelectedOrganization } from '@shared/types/auth/selected-organization.type';
import { allowedRoles } from '@core/consts/allowed-roles';
import { parseRoleName } from '@shared/helpers/role-naming-parser/role-naming-parser';

@Injectable({
  providedIn: 'root',
})
export class UserInformationService {
  private readonly graphqlHost = inject(GraphqlHostService);
  private readonly authenticationService = inject(AuthenticationService);

  private readonly userOrganizationRoles$ = new BehaviorSubject<
    UserOrganization[] | null
  >(null);
  private readonly userProfile = new BehaviorSubject<UserById | null>(null);

  // selectionDefaults
  private readonly selectedOrganization$ =
    new BehaviorSubject<SelectedOrganization>({
      orgId: null,
      orgCode: null,
      orgName: null,
    });

  private readonly selectedRole$ = new BehaviorSubject<OrganizationRole | null>(
    null,
  );

  private readonly accountSelectorOptions =
    new BehaviorSubject<ParsedAccountSelectorOptions | null>(null);

  readonly SERVER = environment.graphql.rbs.name;
  readonly env = environment.env;
  readonly requiredMicroservices = environment.requiredMicroservices;
  readonly roleParserHelper = parseRoleName;

  async fetchUserDataFromRBSByID(policy: FetchPolicy = 'network-only') {
    const vars = { id: this.authenticationService.userId.value };
    try {
      const response = await firstValueFrom(
        this.graphqlHost.query(
          this.SERVER,
          userOrganization.GET_USER_PROFILE,
          vars,
          policy,
        ),
      );
      const data = response?.data as ProfileData;
      // build user organization in a way that is easier to consume
      const userOrganizationData = this.OrganizationRoleBuilder(data?.userById);

      // populate and share user data
      this.userProfile.next(data?.userById);
      this.userOrganizationRoles$.next(userOrganizationData);

      this.accountSelectorData(data?.selector?.options?.[0]?.value ?? null);
      return data?.userById;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  }

  async fetchAllowedOrganizations(
    organizations: string[],
    policy: FetchPolicy = 'network-only',
  ): Promise<boolean | null> {
    try {
      const data = await this.fetchAuthorizedOrganizationsData(
        organizations,
        policy,
      );

      if (!this.hasAuthorizedOrganizations(data)) {
        return false;
      }

      const filteredOrgs = this.filterUserOrganizations(data);

      if (!filteredOrgs?.length) {
        return false;
      }

      return this.setOrganizationAndRoleSelection(data, filteredOrgs);
    } catch (error) {
      console.error('Error fetching allowed organizations:', error);
      return null;
    }
  }

  private async fetchAuthorizedOrganizationsData(
    organizations: string[],
    policy: FetchPolicy,
  ): Promise<OrganizationData> {
    const vars = {
      where: {
        id: {
          in: organizations,
        },
        orgsInServices: {
          some: {
            serviceId: {
              in: this.requiredMicroservices,
            },
          },
        },
      },
    };

    const response = await firstValueFrom(
      this.graphqlHost.query(
        this.SERVER,
        userOrganization.GET_AUTHORIZED_ORGS,
        vars,
        policy,
      ),
    );

    return response?.data as OrganizationData;
  }

  private hasAuthorizedOrganizations(data: OrganizationData): boolean {
    return !!data.organizations?.edges?.length;
  }

  private filterUserOrganizations(
    data: OrganizationData,
  ): UsersInOrgEntity[] | undefined {
    return this.userProfile.value?.usersInOrg?.filter((userOrg) =>
      data.organizations.edges?.some(
        (orgEdge) => orgEdge.node.id === userOrg.orgId,
      ),
    );
  }

  private setOrganizationAndRoleSelection(
    data: OrganizationData,
    filteredOrgs: UsersInOrgEntity[],
  ): boolean {
    if (this.accountSelectorOptions.value) {
      const isValidSelection =
        this.setSelectionFromAccountSelector(filteredOrgs);
      if (isValidSelection !== null) {
        return isValidSelection;
      }
    }

    if (this.shouldAutoSelectOrganization()) {
      return this.setDefaultOrganizationSelection(data);
    }

    return false;
  }

  private setSelectionFromAccountSelector(
    filteredOrgs: UsersInOrgEntity[],
  ): boolean | null {
    const selectedOrgData = this.buildUserSelectionsFromAccountSelector(
      this.accountSelectorOptions.value!,
      filteredOrgs,
    );

    if (!selectedOrgData) {
      return null;
    }

    this.setSelectedOrganization(
      selectedOrgData.orgId,
      selectedOrgData.orgCode,
      selectedOrgData.orgName,
    );

    this.selectedRole$.next({
      orgRoleId: this.accountSelectorOptions.value!.UserOrgInOrgRolesId,
      serviceRoleId: selectedOrgData.roleId,
      serviceRoleName: selectedOrgData.roleName,
    });

    return this.isSelectedRoleAllowed();
  }

  private shouldAutoSelectOrganization(): boolean {
    const envLower = this.env.toLowerCase();
    return envLower !== 'prod' && envLower !== 'demo';
  }

  private setDefaultOrganizationSelection(data: OrganizationData): boolean {
    const { id, orgCode, orgName } = data.organizations.edges![0].node;
    const defaultOrg = this.userOrganizationRoles$.value?.find(
      (org) => org.organizationId === id,
    );

    this.setSelectedOrganization(id, orgCode, orgName);
    this.selectedRole$.next(defaultOrg?.roles[0] ?? null);

    return this.isSelectedRoleAllowed();
  }

  private isSelectedRoleAllowed(): boolean {
    return allowedRoles.includes(
      this.selectedRole$.value?.serviceRoleName ?? '',
    );
  }

  private buildUserSelectionsFromAccountSelector(
    options: ParsedAccountSelectorOptions,
    userOrganization: UsersInOrgEntity[],
  ): RoleSelectorRetrievedData | null {
    const { userInOrgId, UserOrgInOrgRolesId } = options;

    const matchedUserInOrg = userOrganization.find(
      (userInOrg) => userInOrg.id === userInOrgId,
    );

    if (!matchedUserInOrg) {
      return null;
    }

    const matchedRole = matchedUserInOrg.orgUsersForOrgRole?.find(
      (orgRole) => orgRole.id === UserOrgInOrgRolesId,
    );

    if (!matchedRole) {
      return null;
    }

    return {
      orgId: matchedUserInOrg.orgId,
      orgCode: matchedUserInOrg.org.orgCode,
      orgName: matchedUserInOrg.org.orgName,
      roleId: matchedRole.orgRole.serviceRoleId,
      roleName: matchedRole.orgRole.serviceRole.name,
    };
  }

  private OrganizationRoleBuilder(user: UserById): UserOrganization[] | null {
    if (!user?.usersInOrg) {
      return null;
    }

    const organizationRoles: UserOrganization[] = user.usersInOrg.map(
      ({
        id: userInOrganizationId,
        orgId: organizationId,
        org: { orgCode, orgName, orgType },
        orgUsersForOrgRole,
      }) => {
        const roles =
          orgUsersForOrgRole?.map(
            ({
              id: orgRoleId,
              orgRole: {
                serviceRoleId,
                serviceRole: { name: serviceRoleName },
              },
            }) => ({
              orgRoleId,
              serviceRoleId,
              serviceRoleName,
            }),
          ) || [];

        return {
          roles,
          userInOrganizationId,
          organizationId,
          orgCode,
          orgName,
          orgType,
        };
      },
    );
    return organizationRoles;
  }

  private setSelectedOrganization(
    orgId: string | null,
    orgCode: string | null,
    orgName: string | null,
  ): void {
    this.selectedOrganization$.next({
      orgId,
      orgCode,
      orgName,
    });
  }

  private accountSelectorData(selectorOptions: string | null): void {
    if (!selectorOptions) return;
    let accountSelectorOptions: ParsedAccountSelectorOptions | null = null;
    try {
      accountSelectorOptions = JSON.parse(selectorOptions);
      this.accountSelectorOptions.next(accountSelectorOptions);
    } catch (error) {
      console.error('Error parsing account selector options:', error);
    }
  }

  get userProfileData(): BehaviorSubject<UserById | null> {
    return this.userProfile;
  }

  get userOrganizationRoles(): BehaviorSubject<UserOrganization[] | null> {
    return this.userOrganizationRoles$;
  }

  get selectedTenantId(): BehaviorSubject<string | null> {
    return new BehaviorSubject(this.selectedOrganization$.value.orgId);
  }

  get selectedOrgCode(): BehaviorSubject<string | null> {
    return new BehaviorSubject(this.selectedOrganization$.value.orgCode);
  }

  get selectedOrgName(): BehaviorSubject<string | null> {
    return new BehaviorSubject(this.selectedOrganization$.value.orgName);
  }

  get selectedOrganization(): BehaviorSubject<SelectedOrganization> {
    return this.selectedOrganization$;
  }

  get selectedRole(): BehaviorSubject<OrganizationRole | null> {
    return this.selectedRole$;
  }

  get UserSelectedRoleAndOrg(): string {
    const role =
      this.selectedRole$.value?.serviceRoleName ?? 'No Role Selected';
    const org =
      this.selectedOrganization$.value.orgName ?? 'No Organization Selected';
    return `${org}  |  ${this.roleParserHelper(role)}`;
  }
}
